// src/components/ProductCard.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Tooltip as ReactTooltip } from "react-tooltip";
import { ProductCardProps, displayPrice, useHandleAddToCart, Customer, User, Product, Modifier, ProductFlag } from '../utils/types'; // Added Modifier
import { useSpring, animated } from 'react-spring';
import { useInView } from 'react-intersection-observer';
import { IconContext } from "react-icons";
import { FaShieldHeart, FaLink } from "react-icons/fa6";
import ProductModal from './ProductModal';
import ModifierModal from './ModifierModal';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useCustomerContext } from '../context/customerContext';
import { hasRole } from '../utils/auth';
import CustomerProfileModal from './CustomerProfileModal'; // Import the modal
import { toast } from 'react-toastify'; // For notifications
import GetMTPriceS from './GetMTPriceS'; // Import the GetMTPriceS component
import { appSettings } from '../utils/api';
import { useConfig } from '../context/ConfigContext'; // Import useConfig
import { useTranslation } from 'react-i18next'; // Import useTranslation


// Define a type for the price data expected from the parent
interface MTPriceData {
    price: number;
    role: string; // The role for which this price applies
}

// Update ProductCardProps to include customer data for action check
interface EnhancedProductCardProps extends ProductCardProps {
    mtPriceData: MTPriceData | null | undefined;
    effectiveUserId: number | undefined;
    effectiveUserRole: string | undefined;
    effectiveUserIsAuthenticated: boolean;
    customerForAction: Customer | null; // Receive the full customer object
    isLoadingCustomerForAction: boolean; // Receive loading state
    loadingPrices: boolean; // <-- Add loadingPrices prop

}


const ProductCard: React.FC<EnhancedProductCardProps> = ({
    product,
    mtPriceData,
    effectiveUserId,
    effectiveUserRole,
    effectiveUserIsAuthenticated,
    customerForAction, // Destructure customer data
    isLoadingCustomerForAction, // Destructure loading state
    loadingPrices // <-- Destructure loadingPrices prop

}) => {
    const { t } = useTranslation(); // Initialize useTranslation

    // const settings = JSON.parse(localStorage.getItem('settings') || '{}'); // Get settings from local storage
    const { settings, loadingSettings } = useConfig(); // Use settings from context

    // if (loadingSettings || !settings || !settings.themes || typeof settings.themes.theme_name !== 'string' || typeof settings.themes.displayPrice !== 'boolean' || typeof settings.themes.displayStockQty !== 'boolean') {
    //     // console.warn("Footer: Settings, themes, or theme_name not available. Skipping CSS load.");
    //     return; // Exit if settings are not ready or theme_name is missing
    // }

    const [showProductModal, setShowProductModal] = useState(false); // Renamed state for clarity
    const { handleAddToCart, showModifierModal, handleCloseModifierModal, modifiers, selectedProduct, handleModifierSelection, handleConfirmModifiers, selectedModifiers } = useHandleAddToCart();
    const { ref, inView } = useInView({ rootMargin: '300px', threshold: 0.1 });
    const [hasImageLoaded, setHasImageLoaded] = useState(false);
    const imageRef = useRef<HTMLImageElement>(null);
    const [quantity, setQuantity] = useState(1); // State for quantity

    // --- State for Customer Profile Modal ---
    const [showCustomerProfileModal, setShowCustomerProfileModal] = useState(false);

    useEffect(() => {
        if (inView && !hasImageLoaded) setHasImageLoaded(true);
    }, [inView, hasImageLoaded]);

    const props = useSpring({
        transform: inView ? 'scale(1)' : 'scale(0.5)',
        opacity: inView ? 1 : 0.5,
        config: { tension: 100, friction: 20 },
    });

    // Add this inside your ProductCard component
    useEffect(() => {
        if (showCustomerProfileModal) {
            // console.log("useEffect: showCustomerProfileModal is now true. customerForAction:", customerForAction);
        } else {
            // console.log("useEffect: showCustomerProfileModal is now false.");
        }
    }, [showCustomerProfileModal, customerForAction]); // Add customerForAction as dependency too

    // --- Quantity Change Handler ---
    const handleQuantityChange = (newQuantity: number) => {
        setQuantity(Math.max(1, newQuantity)); // Ensure quantity is at least 1
    };

    // --- Updated Add to Cart Click Handler ---
    const handleAddToCartClick = () => {
        // 1. Check Authentication
        if (!effectiveUserIsAuthenticated) {
            toast.error("Please log in to add items to CART.");
            return;
        }

        // 2. Check if customer data is still loading
        if (isLoadingCustomerForAction) {
            toast.info("Checking customer details...");
            return; // Wait for customer data to load
        }

        // 3. Check if customer data exists and has phone/email
        if (!customerForAction) {
            toast.error("Could not verify customer details. Please try again or contact support.");
            console.error("Add to cart check failed: customerForAction is null or undefined");
            return;
        }

        const hasPhone = customerForAction.phone && String(customerForAction.phone).trim() !== '';
        const hasEmail = customerForAction.email && String(customerForAction.email).trim() !== '';

        if (!hasPhone || !hasEmail) {
            // 4. If missing, show the profile modal
            toast.warn("Please complete your profile (Phone and Email) before adding items.");
            setShowCustomerProfileModal(true);
            // !!! This log runs BEFORE React re-renders with the new state value !!!
            // console.log("customerForAction : ", customerForAction, showCustomerProfileModal);
        } else {
            // 5. If present, proceed with adding to cart
            if (imageRef.current) {
                const priceToAdd = mtPriceData?.price ?? product.product_value;
                // handleAddToCart(product, imageRef, priceToAdd, effectiveUserId, effectiveUserRole);
                handleAddToCart(product, imageRef, priceToAdd, effectiveUserId, effectiveUserRole, quantity);

            } else {
                console.error('ProductCard: imageRef.current is null or undefined!');
                toast.error("An error occurred. Please try again.");
            }
        }
    };
    // --- End Updated Handler ---

    const handleProductModalOpen = () => setShowProductModal(true);
    const handleProductModalClose = () => setShowProductModal(false);

    // --- Handler to close the Customer Profile Modal ---
    const handleCustomerProfileModalClose = () => {
        setShowCustomerProfileModal(false);
        // Optional: Re-fetch customer data here or rely on parent (Home) to update if needed
        // Consider if the modal itself should trigger a refresh on successful update.
    };

    const truncateProductName = (name: string, maxLength: number) => name.length > maxLength ? name.slice(0, maxLength) + '...' : name;
    const truncateProductDesc = (desc: string | null, maxLength: number) => desc && desc.length > maxLength ? desc.slice(0, maxLength) + '...' : desc;

    function toTitleCase(str: string | undefined) {
        if (!str) return '';
        return str.replace(/_/g, ' ')
            .replace(/\w\S*/g, text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase());
    }

    // console.log("mtPriceData:", mtPriceData);
    // console.log("effectiveUserIsAuthenticated:", effectiveUserIsAuthenticated);
    // console.log("effectiveUserId:", effectiveUserId); 
    // console.log("hasrole:", hasRole(['super_admin', 'staff', 'technician'])); 
    // console.log("MTPriceArray:", product); 

    // Custom hook to get responsive truncation length
    function useResponsiveDescLength() {
        const [descLength, setDescLength] = useState(95);

        useEffect(() => {
            function updateLength() {
                const width = window.innerWidth;
                if (width < 400) setDescLength(40); // very small screens
                else if (width < 640) setDescLength(60); // mobile
                else if (width < 1024) setDescLength(80); // tablet
                else setDescLength(95); // desktop
            }
            updateLength();
            window.addEventListener('resize', updateLength);
            return () => window.removeEventListener('resize', updateLength);
        }, []);

        return descLength;
    }

    // ...inside ProductCard component...
    const descLength = useResponsiveDescLength();


    // Custom hook to get responsive truncation length
    function useResponsiveProductNameLength() {
        const [productNameLength, setProductNameLength] = useState(68);

        useEffect(() => {
            function updateLength() {
                const width = window.innerWidth;
                if (width < 400) setProductNameLength(40); // very small screens
                else if (width < 640) setProductNameLength(40); // mobile
                else if (width < 1024) setProductNameLength(36); // tablet
                else setProductNameLength(68); // desktop
            }
            updateLength();
            window.addEventListener('resize', updateLength);
            return () => window.removeEventListener('resize', updateLength);
        }, []);

        return productNameLength;
    }

    // ...inside ProductCard component...
    const productNameLength = useResponsiveProductNameLength();

    // console.log('themes:', settings?.themes);

    // type ProductFlag = 'NEW' | 'PROMOTION' | 'HOT';

    // const flags: ProductFlag[] = [];
    // if (product.is_new) flags.push("NEW");
    // if (product.is_promotion) flags.push("PROMOTION");
    // if (product.is_hot) flags.push("HOT");

    const flagStyles: Record<ProductFlag, string> = {
        NEW: "bg-green-500 text-white animate-jump-in",
        PROMOTION: "bg-yellow-400 text-red-900 animate-wiggle",
        HOT: "bg-red-600 text-white animate-pulse animate-infinite animate-ease-in-out",
    };


    return (
        <div className="w-1/2 sm:w-1/3 md:w-1/4 lg:w-1/5 border border-b-2 border-b-gray-400 rounded px-1 py-1 mx-0 my-1">
            {/* Image Section */}
            <div className="w-full h-48 md:w-full md:h-48 rounded p-1 flex flex-col justify-center items-center" ref={ref} onClick={handleProductModalOpen}>

                <animated.div style={props}>


                    {/* Badge(s) */}
                    <div className="absolute top-2 right-2 flex flex-col md:flex-row gap-1">
                        {/* {flags.map((flag) => (
                            <span
                                key={flag}
                                className={`px-3 py-1 text-xs font-bold uppercase rounded-full shadow-md ${flagStyles[flag]}`}
                            >
                                {flag}
                            </span>
                        ))} */}

                        {product.flags.map((flag) => (
                            <span key={flag} className={`rounded py-1 px-2 text-xs font-bold uppercase shadow-md ${flagStyles[flag]}`}>

                                {flag}
                            </span>
                        ))}

                    </div>

                    {hasImageLoaded && (
                        <>


                            <motion.img
                                layoutId={`product-image-${product.id}`}
                                src={product.media.length > 0 ? product.media[0].preview_url : window.APP_CONFIG?.PUBLIC_URL + '/placeholder.jpg'}
                                alt={product.product_name}
                                className="product-image px-1 py-1 h-40 md:h-46 object-cover"
                                loading="lazy"
                                ref={imageRef}
                            />

                        </>
                    )}
                </animated.div>

            </div >

            {/* Add to Cart Button */}
            {
                // effectiveUserIsAuthenticated && (
                effectiveUserIsAuthenticated && settings?.themes?.show_add2cart !== false && (

                    // <div className="add2cart-div">
                    <div className="add2cart-div flex flex-col md:flex-row items-center justify-between my-2 px-1">
                        {/* Quantity Adjuster */}
                        <div className="flex items-center">
                            <button
                                onClick={() => handleQuantityChange(quantity - 1)}
                                className="px-3 py-1 border border-gray-300 rounded-l-md hover:bg-gray-100 focus:outline-none text-sm"
                                disabled={isLoadingCustomerForAction || quantity <= 1}
                            >
                                -
                            </button>
                            <input
                            id={'qty-pid-'+product.id.toString()}
                                type="number"
                                value={quantity}
                                onChange={(e) => handleQuantityChange(parseInt(e.target.value, 10) || 1)}
                                className="w-10 text-center border-t border-b border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm py-1"
                                min="1"
                                disabled={isLoadingCustomerForAction}
                            />
                            <button
                                onClick={() => handleQuantityChange(quantity + 1)}
                                className="px-3 py-1 border border-gray-300 rounded-r-md hover:bg-gray-100 focus:outline-none text-sm"
                                disabled={isLoadingCustomerForAction}
                            >
                                +
                            </button>
                        </div>

                        {/* Add to Cart Button */}
                        <button
                            onClick={handleAddToCartClick}
                            // Disable button while checking customer data
                            // disabled={isLoadingCustomerForAction}
                            // className={`add2cart-btn ${isLoadingCustomerForAction ? 'opacity-50 cursor-wait' : ''}`}
                            disabled={isLoadingCustomerForAction}
                            className={`add2cart-btn  m-2 text-xs sm:text-sm ${isLoadingCustomerForAction ? 'opacity-50 cursor-wait' : ''}`}

                        >
                            {isLoadingCustomerForAction ? t('productCard.checking') : t('productCard.addToCart')}
                        </button>
                    </div>
                )
            }

            {/* Modifier Modal */}
            {
                showModifierModal && selectedProduct && (
                    <ModifierModal
                        product={selectedProduct}
                        modifiers={modifiers}
                        onClose={handleCloseModifierModal}
                        onModifierSelection={handleModifierSelection}
                        onConfirm={handleConfirmModifiers}
                        selectedModifiers={selectedModifiers}
                    />
                )
            }

            {/* Product Info */}
            <div className="min-h-16 md:min-h-16 rounded px-2 py-0" >
                <p className="text-xs text-gray-500 font-thin" >{product.cat_name}</p>
                <h3
                    className="text-[0.9em] tracking-[0.08em] text-black font-bold subpixel-antialiased"
                    data-tooltip-id={`name-${product.id}`}
                    data-tooltip-content={product.product_name}
                    onClick={handleProductModalOpen}
                >
                    {truncateProductName(product.product_name, productNameLength)}
                </h3>
                <ReactTooltip id={`name-${product.id}`} place="bottom" style={{ backgroundColor: "yellow", color: "#000000", fontSize: '1rem', zIndex: 50 }} />
            </div>

            {
                product.product_desc && product.product_desc.length > 0 && (
                    <>
                        <div className="min-h-20 rounded px-2 py-2" >
                            <p className="text-[0.8em] tracking-[0.01em] text-gray-700 font-normal break-all"
                                data-tooltip-id={`desc-${product.id}`} data-tooltip-content={product.product_desc || ''}>
                                {truncateProductDesc(product.product_desc, descLength)}
                            </p>
                            {product.product_desc && product.product_desc.length > 95 && <ReactTooltip id={`desc-${product.id}`} place="bottom" style={{ backgroundColor: "green", color: "#FFFFFF", fontSize: '1em', zIndex: 50 }} />}
                        </div>
                    </>
                )
            }

            {/* <div>Supplier: {product.supplier_name}</div> */}

            <div className="min-h-6 rounded px-2 py-0" ref={ref} onClick={handleProductModalOpen}>
                <p className="text-[0.7em] tracking-[0.01em] text-gray-7mt-0 text-blue-600 font-normal break-all"
                >SKU: {product.product_code}</p>
            </div>



            {/* Price Display */}
            {
                // settings?.themes?.displayPrice === true ? (
                settings?.themes?.show_prices !== false ? (


                    <div className="price-div text-right rounded px-2 py-0 ">

                        <p className="text-xs  font-thin text-gray-500 flex flex-row justify-center items-center  md:flex-row md:justify-end md:items-baseline ">
                            {/* <span>{t('productCard.srp')}:&nbsp;</span> */}

                            <span className=' whitespace-nowrap flex flex-nowrap flex-col justify-center items-center  md:flex-row md:justify-end md:items-baseline '>
                                <span>{t('productCard.srp')}:&nbsp;</span>

                                {product.SRP > 0 && product.SRP !== product.product_value ? (
                                    <span className='text-gray-400 font-thin text-lg line-through'>{displayPrice(product.SRP)}</span>
                                ) : null}

                                <span className='text-gray-600 font-bold text-lg px-1'>
                                    {displayPrice(product.product_value)}
                                </span>
                            </span>

                        </p>

                        {effectiveUserIsAuthenticated && mtPriceData ? (
                            <p className="text-xs text-green-600 font-semibold.  flex flex-row justify-center items-center  md:flex-row md:justify-end md:items-baseline">
                                {t('productCard.vipPrice')}:&nbsp;

                                {/* {product.product_value > 0 && mtPriceData && product.product_value > mtPriceData.price ? (
                                    <span className='text-green-600 font-thin text-lg line-through'>{displayPrice(product.product_value)}</span>
                                ) : null} */}

                                <span className='text-green-600 font-bold text-lg px-1'>{displayPrice(mtPriceData.price)}
                                </span>
                            </p>
                        ) : effectiveUserIsAuthenticated ? (

                            <p className="text-xs text-gray-500">No specific price yet.</p>

                        ) : null
                        }



                        {/* --- Skeleton Loading for Price --- */}
                        {loadingPrices && effectiveUserIsAuthenticated && (
                            <div className="animate-pulse flex justify-end items-baseline mb-1">
                                <div className="h-3 bg-gray-200 rounded w-12 mr-1">loading...</div>
                                <div className="h-5 bg-gray-200 rounded w-16">loading...</div>
                            </div>
                        )}
                        {/* --- End Skeleton Loading --- */}

                        {/* THIS IS THE BLOCK YOU'RE INTERESTED IN */}
                        {effectiveUserIsAuthenticated && hasRole(['super_admin', 'staff', 'technician']) && Array.isArray(product.MTPriceArray) && product.MTPriceArray.length > 0 && (
                            <div className="mb-1 bg-indigo-100 p-2 rounded border border-gray-200">
                                <div className="text-xs text-gray-400">Multi Tier Prices:</div>
                                <ul className="text-xs">
                                    {product.MTPriceArray.map((item, idx) => (
                                        <li key={idx}>
                                            <span className="font-thin">{item.cat_name}:</span> <span className="text-green-700">{displayPrice(item.price)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {/* END OF THE RELEVANT BLOCK */}



                    </div>
                ) : null
            }


            {/* Product Modal */}
            {
                showProductModal && (
                    <ProductModal
                        product={product}
                        onClose={handleProductModalClose}
                        // --- Pass the missing props down ---
                        customerForAction={customerForAction}
                        isLoadingCustomerForAction={isLoadingCustomerForAction}
                        mtPriceData={mtPriceData}
                        effectiveUserId={effectiveUserId}
                        effectiveUserRole={effectiveUserRole}
                        effectiveUserIsAuthenticated={effectiveUserIsAuthenticated}
                    // --- End passing props ---
                    />
                )
            }

            {/* Stock/Warranty/Link Info */}
            <div className="flex flex-row justify-between mt-2 ">
                {
                    product?.stock !== undefined && product?.stock > 0 ?
                        <span className="productCardStock px-2 py-1 my-1 text-white bg-green-600 border rounded-lg shadow ">
                            In Stock
                            {settings?.themes?.displayStockQty === true ? (

                                <span className='stock-qty'>&nbsp;{product?.stock}</span>
                            ) : null}
                        </span> :
                        <span className="productCardStock px-2 py-1 my-1 text-white bg-orange-600 border rounded-lg shadow">
                            Back Order
                            {settings?.themes?.displayStockQty === true ? (
                                <span className='backorder-qty'>&nbsp;{product?.stock}</span>
                            ) : null}
                        </span>
                }

                {
                    product?.restock !== undefined && product?.restock > 0 &&
                    <span className="productCardStock px-2 py-1 my-1 text-white bg-purple-600 border rounded-lg shadow flex flex-row" >
                        Re-Stock
                        {/* <pre>{JSON.stringify({ displayRestockAt: settings?.themes?.displayRestockAt, restock_at: product.restock_at })}</pre> */}

                        {settings?.themes?.displayStockQty === true ? (
                            <>
                                <span className='restock-qty'>&nbsp;{product?.restock}</span>
                            </>
                        ) : null}
                        {settings?.themes?.displayRestockAt === true && product.restock_at ? (
                            <span className='restock-dt'>
                                &nbsp;on {new Date(product.restock_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                            </span>
                        ) : null}
                    </span>
                }

                <div className='flex flex-row justify-between items-center mt-2'>
                    {product.product_link && <a href={product.product_link} className='mr-2' target="_blank" rel="noreferrer" data-tooltip-id={`link-${product.id}`} data-tooltip-content={`External ${product.product_name} Product Details Link`}><IconContext.Provider value={{ size: "1em", color: "purple" }}><FaLink /></IconContext.Provider></a>}
                    {product.product_link && <ReactTooltip id={`link-${product.id}`} place="top" style={{ backgroundColor: "yellow", color: "#000000", zIndex: 50 }} />}
                    {product.warranty && <div data-tooltip-id={`warranty-${product.id}`} data-tooltip-content={`${product.warranty} warranty`}><IconContext.Provider value={{ size: "1em", color: "brown" }}><FaShieldHeart /></IconContext.Provider></div>}
                    {product.warranty && <ReactTooltip id={`warranty-${product.id}`} place="top" style={{ backgroundColor: "yellow", color: "#000000", zIndex: 50 }} />}
                </div>
            </div>

            {/* --- Render Customer Profile Modal Conditionally --- */}
            {
                (() => {
                    // Add this temporary log
                    if (showCustomerProfileModal) {
                        // console.log("Rendering check: showCustomerProfileModal=true, customerForAction=", customerForAction);
                    }
                    // Original rendering logic
                    return showCustomerProfileModal && customerForAction && (

                        // <div style={{ position: 'fixed', top: '30%', left: '30%', width: '40%', height: '40%', background: 'lightblue', border: '2px solid red', zIndex: 9999 }}>
                        //     MODAL SHOULD BE HERE!
                        //     <button onClick={handleCustomerProfileModalClose}>Close Simple</button>
                        // </div>

                        // <div style={{ position: 'fixed', top: '5%', left: '0%', width: '100%', zIndex: 9999 }}>
                        <CustomerProfileModal

                            initialCustomerData={customerForAction}
                            onClose={handleCustomerProfileModalClose}
                        />
                        // </div>
                    );
                })()
            }
            {/* --- End Customer Profile Modal --- */}
        </div >
    );
};

export default ProductCard;
